import type { KanbanStatus } from '../types/kanban.js';

/**
 * ENG-5730 — the single source of truth for what a kanban status transition
 * *means*, replacing the rules that were scattered inline across the
 * `POST /host/kanban` handler in `packages/api/src/routes/host-runtime.ts`.
 *
 * `transition()` is intentionally **pure**: no DB, no I/O. The API layer reads
 * the current status, calls `transition()` to validate + classify the move,
 * applies the row write it already performs today, and (on a real status
 * change) appends a `kanban_events` row. Keeping the policy here makes it unit
 * testable and lets future writers (reaper, console PATCH) adopt the same
 * contract.
 *
 * Design (see the ENG-5730 plan review): the table is deliberately PERMISSIVE,
 * mirroring today's behaviour where the handler accepts any valid status → any
 * valid status. We reject only the two moves that are unambiguously wrong:
 *   1. an unknown status string, and
 *   2. "resurrecting" a closed card — `done | failed | cancelled` back to an
 *      active state (`backlog | todo | in_progress`).
 * Everything else (direct jumps like `backlog → done`, terminal reshuffles like
 * `done → failed`) stays allowed so existing API contracts don't change.
 */

/** The full status set, aligned with the agent_kanban_items DB CHECK. */
export const KANBAN_STATUSES = [
  'backlog',
  'todo',
  'in_progress',
  'done',
  'failed',
  'cancelled',
  'needs_attention',
  'waiting',
] as const;

/**
 * Active (open) states a card can be worked from. `waiting` (ENG-7493 /
 * ADR-0044) is deliberately NOT here: a parked card must fall out of the
 * work-loop "resume in_progress" pickup and the 30-min in_progress auto-fail,
 * which it does for free by not being an active state.
 */
export const KANBAN_ACTIVE_STATES: ReadonlySet<KanbanStatus> = new Set<KanbanStatus>([
  'backlog',
  'todo',
  'in_progress',
]);

/**
 * Closed states that must not be resurrected back to an active state by an
 * agent write. `needs_attention` is intentionally NOT here: although the reaper
 * treats it as terminal, an operator/user (and the agent itself, once the issue
 * is addressed) can legitimately revive it. It is therefore a normal active-ish
 * state from the state machine's perspective and never blocks a move.
 *
 * `waiting` (ENG-7493 / ADR-0044) is also intentionally NOT here: the auto-return
 * `waiting → in_progress` (blocker cleared) MUST stay legal, so `waiting` must
 * not be resurrection-blocked. It is neither active nor blocked, just a parked
 * card that any writer can move back into work.
 */
export const KANBAN_RESURRECTION_BLOCKED: ReadonlySet<KanbanStatus> = new Set<KanbanStatus>([
  'done',
  'failed',
  'cancelled',
]);

/**
 * Terminal states — a card in one of these is finished and nobody is looking at
 * it any more. Distinct from KANBAN_RESURRECTION_BLOCKED, which happens to hold
 * the same members but answers a different question ("may this be revived?").
 * Kept separate so changing one does not silently change the other.
 */
export const KANBAN_TERMINAL_STATES: ReadonlySet<KanbanStatus> = new Set<KanbanStatus>([
  'done',
  'failed',
  'cancelled',
]);

/**
 * Parked states: nobody is working the card and the whole point is that somebody
 * ELSE is expected to see it. Today that is just `waiting` (ENG-7493 / ADR-0044).
 *
 * ENG-8457: this is the set that automated writers may not terminate. A parked
 * card is the one kind of card where a wrongful close is unnoticeable BY
 * CONSTRUCTION — an `in_progress` card closed early is caught because the agent
 * is still holding it, but nobody is watching a `waiting` card, because waiting
 * MEANS nobody is watching.
 */
export const KANBAN_PARKED_STATES: ReadonlySet<KanbanStatus> = new Set<KanbanStatus>(['waiting']);

/**
 * Who is asking for a transition. ENG-8457 — the state machine was previously
 * actor-blind, which is why an automated writer could terminate a parked card
 * carrying an unread human hand-off.
 *
 * - `agent`    — the owning agent's own explicit tool call (kanban_done /
 *                kanban_move). The agent owns its card's terminal transition.
 * - `human`    — a person acting deliberately (console PATCH, board drag, a
 *                human-assigned task completion). Trusted to close anything;
 *                a human closing a parked card IS the loop working.
 * - `automated` — anything else: run-lifecycle wrappers, reapers, cron sweeps,
 *                upstream-import reconcilers, prose-parsed board side effects.
 *                No intent behind it, so it must not destroy a hand-off.
 */
export type KanbanWriteIntent = 'agent' | 'human' | 'automated';

/**
 * Context for a transition request. ENG-8457.
 *
 * `hasExplanation` exists because actor attribution turned out to be unavailable
 * where it is needed. At the `/host/kanban` write path, an agent's own MCP
 * `kanban_done`, an operator's `agt kanban done`, and any host-resident script
 * all authenticate with the same HOST api key and therefore all resolve to
 * `agent:<id>` — they are indistinguishable in the row AND in the kanban_events
 * ledger. So a rule of the form "only the agent itself may close a parked card"
 * cannot actually be enforced today.
 *
 * This is the rule that CAN be: terminating a parked card must SAY SOMETHING.
 * It catches the incident by its real signature — the close carried an empty
 * result — without needing to know who did it, and it is the property that
 * actually matters. A parked card's body is an unread hand-off; closing it with
 * nothing to show is the move that destroys information. A caller with a genuine
 * outcome is closing it deliberately and is let through.
 */
export interface TransitionContext {
  intent: KanbanWriteIntent;
  /**
   * ENG-9050 — this write is an ASSIGNER REOPENING a card it delegated, with
   * review feedback attached. The one sanctioned exception to the resurrection
   * rule below.
   *
   * WHAT THE STATE MACHINE DOES AND DOES NOT CHECK. It does not, and cannot,
   * verify that the caller really is the assigner — the same reason
   * `hasExplanation` exists rather than an actor rule (every host-authenticated
   * writer resolves to `agent:<id>` and they are indistinguishable here). The
   * provenance check (`metadata.assigned_from_agent_id === caller`, same team,
   * card is terminal) is the ROUTE's job and must happen before this flag is
   * set. This layer records that the exception was claimed and constrains how
   * far it can reach.
   *
   * It is constrained two ways, both deliberate:
   *   - only to `todo`. A reopen hands work back to a board, it does not resume
   *     an in-flight task, so `done → in_progress` stays blocked for everyone.
   *   - only WITH an explanation. A reopen carrying no feedback is precisely the
   *     information-destroying close ENG-8457 exists to stop, pointed the other
   *     way — and a correction with nothing in it is not a correction. The
   *     feature's whole purpose supplies its own guard.
   */
  assignerReopen?: boolean;
  /**
   * Whether this write carries a non-empty `result` OR `notes`. Either counts:
   * a successful close explains itself in `result`, and a
   * `kanban_move(status='failed')` explains itself in `notes`.
   *
   * Compute it with {@link explains} rather than by hand — see that function for
   * why `Boolean(value)` is the wrong test.
   */
  hasExplanation?: boolean;
}

/**
 * Does any of these values actually say something?
 *
 * CodeRabbit on PR #4245. The call sites computed `hasExplanation` as
 * `Boolean(upd.result) || Boolean(upd.notes)`, which is `true` for `"   "`. So a
 * caller could terminate a parked card with `result: "   "` and satisfy the
 * "a close must explain itself" rule while writing nothing readable — defeating
 * the ENG-8457 guard with a space bar.
 *
 * It is not hypothetical for `notes`: the notes appender downstream strips
 * control characters and collapses whitespace, then returns the existing notes
 * unchanged when what is left is empty. A whitespace-only note therefore appends
 * no breadcrumb, emits no `note` event, and still counted as an explanation.
 *
 * This lives HERE, next to the rule it serves, rather than being trimmed at each
 * call site, because "what counts as saying something" is part of the transition
 * policy — not caller plumbing. Two call sites computing it inline is already
 * how it drifted; a third would inherit the bug silently. One definition, unit
 * tested with the rule it belongs to.
 */
export function explains(...values: Array<string | null | undefined>): boolean {
  return values.some((v) => typeof v === 'string' && v.trim().length > 0);
}

const KANBAN_STATUS_SET: ReadonlySet<string> = new Set<string>(KANBAN_STATUSES);

/** Narrowing guard for an arbitrary string against the canonical status set. */
export function isKanbanStatus(value: unknown): value is KanbanStatus {
  return typeof value === 'string' && KANBAN_STATUS_SET.has(value);
}

export type TransitionFailureCode =
  | 'unknown_status'
  | 'invalid_transition'
  /**
   * ENG-8457: an automated writer tried to terminate a parked card. The correct
   * automated moves out of `waiting` are back to an active state or to
   * `needs_attention` (which is what the stale-waiting reaper already does) —
   * never straight to done/failed.
   */
  | 'automated_terminal_from_parked'
  /**
   * ENG-8457: something tried to terminate a parked card while carrying neither
   * a `result` nor `notes` — i.e. it would replace an unread hand-off with an
   * empty deliverable. That is the exact signature of the incident: a card in
   * `waiting`, closed to `done`, with nothing in `result`.
   */
  | 'terminal_from_parked_without_explanation';

/**
 * Result of {@link transition}. `changed` distinguishes a real status move
 * (`from !== to`) from an idempotent re-write (`from === to`). Callers append a
 * `kanban_events` row only when `ok && changed` — a `done → done` re-issue or a
 * notes/progress-only update must NOT produce an event (it would flood the
 * append-only ledger with no-signal heartbeats).
 */
export type TransitionResult =
  | { ok: true; from: KanbanStatus | null; to: KanbanStatus; changed: boolean }
  | {
      ok: false;
      code: TransitionFailureCode;
      from: KanbanStatus | null;
      attempted: string;
    };

/**
 * Validate and classify a kanban status transition.
 *
 * @param from the card's current status, or `null` for a brand-new card (the
 *   add path) — a null `from` permits any valid initial status.
 * @param to   the requested next status (raw string; validated here).
 * @param ctx who is asking and whether the write explains itself — see
 *   {@link TransitionContext}. REQUIRED rather than defaulted: ENG-8457 happened
 *   because a write path had no notion of intent at all, and a default would let
 *   the next new call site inherit the permissive answer silently. Making every
 *   caller state it forces the decision to be visible in review.
 */
export function transition(
  from: KanbanStatus | null,
  to: string,
  ctx: TransitionContext,
): TransitionResult {
  if (!isKanbanStatus(to)) {
    return { ok: false, code: 'unknown_status', from, attempted: to };
  }

  // Add path: a new card may start in any valid status (parity with today's
  // permissive add, which accepts e.g. an item created directly as `done`).
  if (from === null) {
    return { ok: true, from: null, to, changed: true };
  }

  // Idempotent re-write — allowed, but flagged as no-change so the caller skips
  // the event write. Covers `done → done` completion re-issues that the
  // confirmation idempotency gate depends on succeeding.
  if (from === to) {
    return { ok: true, from, to, changed: false };
  }

  // Resurrecting a closed card back to active work. Terminal reshuffles (e.g.
  // `done → failed`) stay allowed.
  if (KANBAN_RESURRECTION_BLOCKED.has(from) && KANBAN_ACTIVE_STATES.has(to)) {
    // ENG-9050: the ONE exception — an assigner reopening delegated work with
    // feedback. See `TransitionContext.assignerReopen` for why the state machine
    // trusts the flag but bounds what it can do.
    //
    // Deliberately NOT a general relaxation. Every other writer, and every other
    // target status, still hits the rule below unchanged, so the invariant
    // ENG-7767 and ENG-8457 protect is untouched: a closed card cannot quietly
    // return to active work.
    //
    // `cancelled` is deliberately NOT reopenable. `done` and `failed` are the
    // states a delegated card reaches by being WORKED — the outcomes a reviewer
    // may legitimately disagree with. `cancelled` means somebody decided the
    // work should not happen at all, and it is reachable from the Linear import
    // adapter, so reopening it would fight the upstream system rather than
    // correct a teammate. Caught by the test suite before it shipped: my first
    // cut allowed any resurrection-blocked source.
    const isReopenableSource = from === 'done' || from === 'failed';
    const isSanctionedReopen =
      ctx.assignerReopen === true &&
      isReopenableSource &&
      to === 'todo' &&
      ctx.hasExplanation === true;
    if (!isSanctionedReopen) {
      return { ok: false, code: 'invalid_transition', from, attempted: to };
    }
  }

  // ENG-8457 — protect a parked card from an automated terminal write.
  //
  // A card in `waiting` carries the entire hand-off: which org owns the work, the
  // routing options, the blocking questions. Closing it does not just lose a
  // status, it deletes an ask nobody has read yet, and it is invisible because
  // nothing is watching a parked card. The incident that motivated this had a
  // full customer hand-off one sweep away from vanishing.
  //
  // Deliberately WIDER than ENG-8457's AC3, which asks only for
  // `waiting -> done`. `waiting -> failed` destroys exactly the same ask, and the
  // one automated path found with no status precondition
  // (`POST /host/runs/finish`) writes `failed` far more often than `done` — every
  // one of its call sites passes outcome `failed`. Blocking done but not failed
  // would have left the actual live hazard open.
  //
  // An automated writer that finds a parked card it thinks is finished should
  // move it back to an active state, or to `needs_attention` for a human — which
  // is what the stale-waiting reaper already does, with a populated reason.
  if (KANBAN_PARKED_STATES.has(from) && KANBAN_TERMINAL_STATES.has(to)) {
    if (ctx.intent === 'automated') {
      return { ok: false, code: 'automated_terminal_from_parked', from, attempted: to };
    }
    // Applies to `agent` and `human` alike, and that is deliberate. See
    // TransitionContext: the three writers that could have produced this incident
    // are indistinguishable at this layer, so a rule keyed on the actor could not
    // have stopped it. A rule keyed on "did this close say anything" does, and it
    // costs a legitimate closer nothing — an agent's kanban_done exists to
    // deliver a result, and `agt kanban done` takes --result/--notes.
    if (!ctx.hasExplanation) {
      return { ok: false, code: 'terminal_from_parked_without_explanation', from, attempted: to };
    }
  }

  return { ok: true, from, to, changed: true };
}
